import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    TextField
} from '@mui/material';
import { Close as CloseIcon, AttachMoney as MoneyIcon, Edit as EditIcon, Save as SaveIcon } from '@mui/icons-material';
import { ClientTariff, clientApi } from '../../services/clientApi';
import { useTheme } from '../../contexts/ThemeContext';

interface ClientTariffsProps {
    open: boolean;
    clientId: number;
    clientName: string;
    onClose: () => void;
    onSuccess?: (message: string) => void;
    onError?: (message: string) => void;
}

interface EditableTariff extends ClientTariff {
    isEditing?: boolean;
}

const ClientTariffs: React.FC<ClientTariffsProps> = ({ open, clientId, clientName, onClose, onSuccess, onError }) => {
    const { theme: appTheme } = useTheme();

    const [tariffs, setTariffs] = useState<EditableTariff[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editedTariffs, setEditedTariffs] = useState<{ [key: string]: EditableTariff }>({});

    useEffect(() => {
        if (open && clientId) {
            loadTariffs();
            setIsEditMode(false);
            setEditedTariffs({});
        }
    }, [open, clientId]);

    const loadTariffs = async () => {
        setLoading(true);
        setError(null);
        try {
            const tariffsData = await clientApi.getClientTariffs(clientId);
            setTariffs(tariffsData);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load tariffs');
        } finally {
            setLoading(false);
        }
    };

    const groupTariffsByService = (tariffs: EditableTariff[]) => {
        const grouped: { [key: string]: EditableTariff[] } = {};

        tariffs.forEach(tariff => {
            const key = `${tariff.service_type} ${tariff.order_type}`;
            if (!grouped[key]) {
                grouped[key] = [];
            }
            grouped[key].push(tariff);
        });

        return grouped;
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-PK', {
            style: 'currency',
            currency: 'PKR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    const getWeightLabel = (weight: string) => {
        if (weight === 'extra') return 'Extra (per kg)';
        // Convert grams to kg for display
        const weightInGrams = parseInt(weight, 10);
        if (!isNaN(weightInGrams)) {
            return `${weightInGrams / 1000} kg`;
        }
        return weight;
    };

    const getTariffKey = (tariff: EditableTariff) => {
        return `${tariff.service_type}-${tariff.order_type}-${tariff.weight}`;
    };

    const handleEditToggle = () => {
        if (isEditMode) {
            // Cancel edit mode - revert changes
            setEditedTariffs({});
        } else {
            // Enter edit mode - initialize edited tariffs
            const edited: { [key: string]: EditableTariff } = {};
            tariffs.forEach(tariff => {
                edited[getTariffKey(tariff)] = { ...tariff };
            });
            setEditedTariffs(edited);
        }
        setIsEditMode(!isEditMode);
    };

    const handleTariffChange = (tariff: EditableTariff, field: 'same_city' | 'same_zone' | 'diff_zone', value: string) => {
        const key = getTariffKey(tariff);
        const numValue = parseFloat(value) || 0;
        setEditedTariffs(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                [field]: numValue
            }
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const tariffsToUpdate = Object.values(editedTariffs).map(tariff => ({
                service_type: tariff.service_type,
                order_type: tariff.order_type,
                weight: tariff.weight,
                same_city: tariff.same_city,
                same_zone: tariff.same_zone,
                diff_zone: tariff.diff_zone,
            }));

            const updatedTariffs = await clientApi.updateClientTariffs(clientId, tariffsToUpdate);
            setTariffs(updatedTariffs);
            setIsEditMode(false);
            setEditedTariffs({});
            onSuccess?.(`Tariffs for "${clientName}" have been updated successfully`);
        } catch (err) {
            onError?.(err instanceof Error ? err.message : 'Failed to update tariffs');
        } finally {
            setSaving(false);
        }
    };

    const getCurrentTariff = (tariff: EditableTariff) => {
        if (isEditMode) {
            const key = getTariffKey(tariff);
            return editedTariffs[key] || tariff;
        }
        return tariff;
    };

    const groupedTariffs = groupTariffsByService(tariffs);

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: appTheme.palette.background.paper,
                        borderRadius: 3,
                    }
                }}
            >
                <DialogTitle sx={{
                    pb: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: `1px solid ${appTheme.palette.divider}`
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <MoneyIcon sx={{ color: appTheme.palette.secondary.main }} />
                        <Box>
                            <Typography variant="h6" sx={{ color: appTheme.palette.text.primary, fontWeight: 600 }}>
                                Client Tariffs
                            </Typography>
                            <Typography variant="body2" sx={{ color: appTheme.palette.text.secondary }}>
                                {clientName}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {!loading && tariffs.length > 0 && (
                            <Button
                                onClick={handleEditToggle}
                                variant={isEditMode ? "outlined" : "contained"}
                                color={isEditMode ? "inherit" : "primary"}
                                startIcon={<EditIcon />}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: 'none'
                                }}
                            >
                                {isEditMode ? 'Cancel' : 'Edit'}
                            </Button>
                        )}
                        <Button
                            onClick={onClose}
                            sx={{
                                color: appTheme.palette.text.secondary,
                                minWidth: 'auto',
                                p: 1
                            }}
                        >
                            <CloseIcon />
                        </Button>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ p: 3 }}>
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {!loading && !error && tariffs.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body1" color="textSecondary">
                                No tariffs found for this client.
                            </Typography>
                        </Box>
                    )}

                    {!loading && !error && tariffs.length > 0 && (
                        <Box>
                            {Object.entries(groupedTariffs).map(([serviceKey, serviceTariffs]) => (
                                <Box key={serviceKey} sx={{ mb: 4 }}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            mb: 2,
                                            color: appTheme.palette.text.primary,
                                            fontWeight: 600,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                        }}
                                    >
                                        <MoneyIcon sx={{ fontSize: 20 }} />
                                        {serviceKey} Tariffs
                                    </Typography>

                                    <TableContainer
                                        component={Paper}
                                        sx={{
                                            bgcolor: appTheme.palette.background.paper,
                                            border: `1px solid ${appTheme.palette.divider}`,
                                            borderRadius: 2
                                        }}
                                    >
                                        <Table>
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: appTheme.palette.action.hover }}>
                                                    <TableCell
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: appTheme.palette.text.primary,
                                                            borderRight: `1px solid ${appTheme.palette.divider}`
                                                        }}
                                                    >
                                                        Weight
                                                    </TableCell>
                                                    <TableCell
                                                        align="center"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: appTheme.palette.text.primary,
                                                            borderRight: `1px solid ${appTheme.palette.divider}`
                                                        }}
                                                    >
                                                        Within City
                                                    </TableCell>
                                                    <TableCell
                                                        align="center"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: appTheme.palette.text.primary,
                                                            borderRight: `1px solid ${appTheme.palette.divider}`
                                                        }}
                                                    >
                                                        Same Zone
                                                    </TableCell>
                                                    <TableCell
                                                        align="center"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: appTheme.palette.text.primary
                                                        }}
                                                    >
                                                        Different Zone
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {serviceTariffs
                                                    .sort((a, b) => {
                                                        const weights = ['500', '1000', '2000', '3000', 'extra'];
                                                        return weights.indexOf(a.weight) - weights.indexOf(b.weight);
                                                    })
                                                    .map((tariff) => {
                                                        const currentTariff = getCurrentTariff(tariff);
                                                        return (
                                                            <TableRow
                                                                key={tariff.id}
                                                                sx={{
                                                                    '&:hover': {
                                                                        bgcolor: appTheme.palette.action.hover
                                                                    }
                                                                }}
                                                            >
                                                                <TableCell
                                                                    sx={{
                                                                        fontWeight: 500,
                                                                        borderRight: `1px solid ${appTheme.palette.divider}`
                                                                    }}
                                                                >
                                                                    {getWeightLabel(tariff.weight)}
                                                                </TableCell>
                                                                <TableCell
                                                                    align="center"
                                                                    sx={{
                                                                        borderRight: `1px solid ${appTheme.palette.divider}`,
                                                                        color: isEditMode ? 'inherit' : appTheme.palette.success.main,
                                                                        fontWeight: 600
                                                                    }}
                                                                >
                                                                    {isEditMode ? (
                                                                        <TextField
                                                                            type="number"
                                                                            size="small"
                                                                            value={currentTariff.same_city}
                                                                            onChange={(e) => handleTariffChange(tariff, 'same_city', e.target.value)}
                                                                            sx={{ width: 100 }}
                                                                            inputProps={{ min: 0, step: 1 }}
                                                                        />
                                                                    ) : (
                                                                        formatCurrency(currentTariff.same_city)
                                                                    )}
                                                                </TableCell>
                                                                <TableCell
                                                                    align="center"
                                                                    sx={{
                                                                        borderRight: `1px solid ${appTheme.palette.divider}`,
                                                                        color: isEditMode ? 'inherit' : appTheme.palette.warning.main,
                                                                        fontWeight: 600
                                                                    }}
                                                                >
                                                                    {isEditMode ? (
                                                                        <TextField
                                                                            type="number"
                                                                            size="small"
                                                                            value={currentTariff.same_zone}
                                                                            onChange={(e) => handleTariffChange(tariff, 'same_zone', e.target.value)}
                                                                            sx={{ width: 100 }}
                                                                            inputProps={{ min: 0, step: 1 }}
                                                                        />
                                                                    ) : (
                                                                        formatCurrency(currentTariff.same_zone)
                                                                    )}
                                                                </TableCell>
                                                                <TableCell
                                                                    align="center"
                                                                    sx={{
                                                                        color: isEditMode ? 'inherit' : appTheme.palette.error.main,
                                                                        fontWeight: 600
                                                                    }}
                                                                >
                                                                    {isEditMode ? (
                                                                        <TextField
                                                                            type="number"
                                                                            size="small"
                                                                            value={currentTariff.diff_zone}
                                                                            onChange={(e) => handleTariffChange(tariff, 'diff_zone', e.target.value)}
                                                                            sx={{ width: 100 }}
                                                                            inputProps={{ min: 0, step: 1 }}
                                                                        />
                                                                    ) : (
                                                                        formatCurrency(currentTariff.diff_zone)
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{
                    p: 3,
                    pt: 2,
                    borderTop: `1px solid ${appTheme.palette.divider}`
                }}>
                    {isEditMode ? (
                        <>
                            <Button
                                onClick={handleEditToggle}
                                variant="outlined"
                                sx={{
                                    borderRadius: 2,
                                    borderColor: appTheme.palette.divider,
                                    color: appTheme.palette.text.secondary,
                                    '&:hover': {
                                        borderColor: appTheme.palette.text.secondary,
                                    }
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSave}
                                variant="contained"
                                color="primary"
                                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                disabled={saving}
                                sx={{
                                    borderRadius: 2,
                                }}
                            >
                                {saving ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </>
                    ) : (
                        <Button
                            onClick={onClose}
                            variant="outlined"
                            sx={{
                                borderRadius: 2,
                                borderColor: appTheme.palette.divider,
                                color: appTheme.palette.text.secondary,
                                '&:hover': {
                                    borderColor: appTheme.palette.text.secondary,
                                }
                            }}
                        >
                            Close
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ClientTariffs;
